import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import { useAuth } from "../App";
import Layout from "../components/Layout";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Users,
  UserPlus,
  Shield,
  ShieldCheck,
  Trash2,
  Mail,
  Clock,
  MoreVertical,
  Crown
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

const TeamPage = () => {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("manager");
  const [inviteLoading, setInviteLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const isAdmin = user?.role === "admin";

  const fetchTeam = useCallback(async () => {
    setLoading(true);
    try {
      const [membersRes, invitesRes] = await Promise.all([
        axios.get(`${API}/team/members`, { withCredentials: true }),
        isAdmin ? axios.get(`${API}/team/invites`, { withCredentials: true }) : Promise.resolve({ data: { invites: [] } })
      ]);
      setMembers(membersRes.data.members || []);
      setInvites(invitesRes.data.invites || []);
    } catch (error) {
      toast.error("Failed to fetch team data");
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchTeam();
  }, [fetchTeam]);

  const handleInvite = async () => {
    if (!inviteEmail) { toast.error("Please enter an email address"); return; }
    setInviteLoading(true);
    try {
      await axios.post(`${API}/team/invite`, { email: inviteEmail, role: inviteRole }, { withCredentials: true });
      toast.success(`Invite sent to ${inviteEmail}`);
      setInviteEmail("");
      setInviteRole("manager");
      setInviteOpen(false);
      fetchTeam();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invite");
    } finally {
      setInviteLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.put(`${API}/team/role`, { user_id: userId, role: newRole }, { withCredentials: true });
      toast.success("Role updated successfully");
      fetchTeam();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update role");
    }
  };

  const handleRemoveMember = async (userId) => {
    try {
      await axios.delete(`${API}/team/member/${userId}`, { withCredentials: true });
      toast.success("Member removed successfully");
      setDeleteConfirm(null);
      fetchTeam();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove member");
    }
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  if (loading) {
    return (
      <Layout title="Team">
        <div className="flex items-center justify-center h-[60vh]">
          <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Team">
      <div className="space-y-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <p className="text-slate-500">{members.length} team member{members.length !== 1 ? "s" : ""} in your organization</p>
          {isAdmin && (
            <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
              <DialogTrigger asChild>
                <Button className="bg-indigo-500 hover:bg-indigo-600 text-white gap-2 shadow-lg shadow-indigo-500/20" data-testid="invite-member-btn">
                  <UserPlus className="w-4 h-4" />
                  Invite Member
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white border-slate-200">
                <DialogHeader>
                  <DialogTitle className="text-slate-900">Invite Team Member</DialogTitle>
                  <DialogDescription className="text-slate-500">Send an invitation to join your support team.</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-slate-700">Email Address</Label>
                    <Input id="email" type="email" placeholder="colleague@company.com" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)}
                      className="bg-white border-slate-200" data-testid="invite-email-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="role" className="text-slate-700">Role</Label>
                    <Select value={inviteRole} onValueChange={setInviteRole}>
                      <SelectTrigger className="bg-white border-slate-200" data-testid="invite-role-select">
                        <SelectValue placeholder="Select role" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-slate-200">
                        <SelectItem value="manager">Manager</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setInviteOpen(false)} className="border-slate-200 text-slate-600">Cancel</Button>
                  <Button onClick={handleInvite} disabled={inviteLoading} className="bg-indigo-500 hover:bg-indigo-600" data-testid="send-invite-btn">
                    {inviteLoading ? "Sending..." : "Send Invite"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>

        <Card className="bg-white border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Users className="w-5 h-5 text-indigo-500" />
              Team Members
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3" data-testid="team-members-list">
              {members.map((member, index) => (
                <motion.div key={member.user_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }}
                  className="flex items-center justify-between p-4 rounded-lg border border-slate-200 bg-slate-50 hover:border-indigo-200 transition-colors">
                  <div className="flex items-center gap-4">
                    <Avatar className="w-11 h-11">
                      <AvatarImage src={member.picture} />
                      <AvatarFallback className="bg-indigo-100 text-indigo-600 font-medium">{getInitials(member.name)}</AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-slate-900">{member.name}</p>
                        {member.user_id === user?.user_id && (
                          <Badge variant="outline" className="text-xs border-indigo-200 text-indigo-600">You</Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-500">{member.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className={member.role === "admin" ? "bg-amber-100 text-amber-700 border-amber-200" : "bg-slate-100 text-slate-600 border-slate-200"}>
                      {member.role === "admin" && <Crown className="w-3 h-3 mr-1" />}
                      {member.role?.charAt(0).toUpperCase() + member.role?.slice(1)}
                    </Badge>
                    {isAdmin && member.user_id !== user?.user_id && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="text-slate-400 hover:text-slate-600">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-white border-slate-200">
                          <DropdownMenuItem onClick={() => handleRoleChange(member.user_id, member.role === "admin" ? "manager" : "admin")} className="text-slate-600">
                            {member.role === "admin" ? (<><Shield className="w-4 h-4 mr-2" />Demote to Manager</>) : (<><ShieldCheck className="w-4 h-4 mr-2" />Promote to Admin</>)}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator className="bg-slate-100" />
                          <DropdownMenuItem onClick={() => setDeleteConfirm(member)} className="text-red-600 focus:text-red-600">
                            <Trash2 className="w-4 h-4 mr-2" />Remove Member
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </CardContent>
        </Card>

        {isAdmin && invites.length > 0 && (
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Mail className="w-5 h-5 text-amber-500" />
                Pending Invites
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3" data-testid="pending-invites-list">
                {invites.map((invite, index) => (
                  <motion.div key={invite.invite_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }}
                    className="flex items-center justify-between p-4 rounded-lg border border-amber-200 bg-amber-50">
                    <div className="flex items-center gap-4">
                      <div className="w-11 h-11 rounded-full bg-amber-100 flex items-center justify-center">
                        <Mail className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{invite.email}</p>
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          <Clock className="w-3.5 h-3.5" />
                          <span>Invited {invite.created_at ? new Date(invite.created_at).toLocaleDateString() : "recently"}</span>
                        </div>
                      </div>
                    </div>
                    <Badge className="bg-amber-100 text-amber-700 border-amber-200">{invite.role?.charAt(0).toUpperCase() + invite.role?.slice(1)}</Badge>
                  </motion.div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
          <AlertDialogContent className="bg-white border-slate-200">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-slate-900">Remove Team Member</AlertDialogTitle>
              <AlertDialogDescription className="text-slate-500">
                Are you sure you want to remove {deleteConfirm?.name} from the team? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="border-slate-200 text-slate-600 hover:bg-slate-50">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={() => handleRemoveMember(deleteConfirm?.user_id)} className="bg-red-500 hover:bg-red-600 text-white">
                Remove Member
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};

export default TeamPage;
